import { notification } from '@block/plug';
import { cloneDeep, datejs as dayjs } from '@block/utils';
import { ValidationParams } from './types';

/**
 * 校验实习生的最高教育经历结束日期（弱校验）
 * @param params 校验参数对象
 */
export const validateInternTopDegreeEndDate = (params: ValidationParams) => {
  const { topDegreeItem, typeName, entryDate } = params;

  if (entryDate && topDegreeItem.endDate && topDegreeItem.endDate <= entryDate) {
    notification.info({
      title: '提示',
      message: `您勾选的最高${typeName}教育经历的结束日期早于或等于入职日期，即入职时已经毕业，请确认信息是否准确，或根据实际情况修改最高${typeName}教育经历的结束日期`,
    });
  }
};

/**
 * 校验教育经历时间是否有重叠（弱校验）
 *
 * @description
 * 该函数检查当前编辑的教育经历与其他教育经历是否存在时间重叠。
 * 特殊处理：允许两段教育经历在同一个月份交接，即使日期上有重叠也不视为时间重叠。
 *
 * @param degreeList 教育经历列表
 * @param newDegreeData 当前正在编辑的教育经历数据（最新值）
 * @param targetIndex 当前正在编辑的教育经历在列表中的索引
 */
export const validateTimeOverlap = (degreeList, newDegreeData, targetIndex) => {
  // 兜底处理：参数校验
  if (!degreeList || !Array.isArray(degreeList)) {
    return;
  }

  // 如果列表为空，则不可能有重叠
  if (degreeList.length === 0) {
    return;
  }

  // 兜底处理：检查必要参数
  if (!newDegreeData) {
    return;
  }

  // 兜底处理：检查时间字段
  if (!newDegreeData.startDate || !newDegreeData.endDate) {
    return;
  }

  // 兜底处理：目标索引
  if (targetIndex === undefined) {
    targetIndex = -1;
  }

  try {
    // 创建副本以避免修改原始数据
    const degreeListCopy = cloneDeep(degreeList);

    // 根据targetIndex替换或添加新数据
    if (targetIndex >= 0 && targetIndex < degreeListCopy.length) {
      // 替换现有数据
      degreeListCopy[targetIndex] = newDegreeData;
    } else {
      // 新增数据
      degreeListCopy.push(newDegreeData);
    }

    // 过滤出有效的教育经历（必须同时有开始和结束日期）
    const validDegrees = degreeListCopy.filter((item) => {
      const { startDate, endDate } = item || {};
      return startDate && endDate;
    });

    // 如果有效记录少于2条，则不可能有重叠
    if (validDegrees.length <= 1) {
      return;
    }

    // 找到当前编辑的记录
    const currentItem = validDegrees.find(
      (item, index) =>
        (targetIndex >= 0 && index === targetIndex) ||
        (item.startDate === newDegreeData.startDate && item.endDate === newDegreeData.endDate),
    );

    // 兜底处理：如果找不到当前编辑的记录
    if (!currentItem) {
      return;
    }

    // 优化算法：只需要一次循环，直接比较当前项与其他所有项
    const overlappingPairs = [];

    // 遍历所有有效记录，找出与当前项重叠的记录
    validDegrees.forEach((otherItem) => {
      // 跳过与自身的比较
      if (otherItem === currentItem) {
        return;
      }

      // 使用dayjs处理日期，提取年月信息
      const currentStartYearMonth = dayjs(currentItem.startDate).format('YYYY-MM');
      const currentEndYearMonth = dayjs(currentItem.endDate).format('YYYY-MM');
      const otherStartYearMonth = dayjs(otherItem.startDate).format('YYYY-MM');
      const otherEndYearMonth = dayjs(otherItem.endDate).format('YYYY-MM');

      // 判断是否为月份交接的情况
      const isMonthTransition =
        currentEndYearMonth === otherStartYearMonth || currentStartYearMonth === otherEndYearMonth;

      // 判断日期是否重叠
      const hasDateOverlap = currentItem.startDate <= otherItem.endDate && currentItem.endDate >= otherItem.startDate;

      // 只有在日期重叠且不是月份交接的情况下，才视为真正的重叠
      if (hasDateOverlap && !isMonthTransition) {
        overlappingPairs.push({
          first: currentItem,
          second: otherItem,
        });
      }
    });

    // 如果存在重叠，显示提示
    if (overlappingPairs.length > 0) {
      notification.info({
        title: '提示',
        message: `教育经历存在时间重叠，请确认是否准确`,
      });
    }
  } catch (error) {
    // 兜底处理：捕获可能的异常
    console.error('validateTimeOverlap: 校验过程中发生错误', error);
  }
};
